import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useState, createContext, forwardRef, useContext, useRef, useLayoutEffect, useCallback, useEffect } from 'react';
import { ChevronUp, ChevronDown, CloseSmall, Search, Check, Close, Info, AlertCircle, AlertTriangle } from '@yleisradio/yds-icons-react';
import { styled, css, keyframes, ThemeProvider, useTheme } from 'styled-components';
import { typography, typographyTokens, color, darkTheme, lightTheme, defaultTheme, radius, spacing } from '@yleisradio/yds-core';
import { useCombobox } from 'downshift';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const sizeMapping = { md: 'M', lg: 'L' };
function font(fontProperty, size) {
    const fontSize = size ? sizeMapping[size] : 'md';
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return typography[`DEFAULT_${fontSize}_BOLD_${fontProperty}`];
}
function accordionPadding(size) {
    switch (size) {
        case 'lg':
            return '20px 16px';
        default:
            return '12px 16px';
    }
}
const AccordionTitle = styled.button(({ theme, $size, $isDisabled }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: font('FONT_SIZE', $size),
    fontWeight: font('FONT_WEIGHT', $size),
    fontStyle: `${font('FONT_STYLE', $size)}`,
    fontFamily: `"${font('FONT_FAMILY', 'md')}", "Open Sans", sans-serif`,
    lineHeight: font('LINE_HEIGHT', $size),
    color: $isDisabled ? theme.yds.TEXT_DISABLED : theme.yds.TEXT_DEFAULT,
    width: '100%',
    padding: accordionPadding($size),
    background: 'none',
    border: 'none',
    cursor: $isDisabled ? 'not-allowed' : 'pointer',
    '&:focus-visible': {
        outline: `3px solid ${theme.yds.BORDER_PRIMARY_FOCUS}`,
    },
    '@media (hover: hover)': {
        '&:hover': {
            backgroundColor: theme.yds.ACTION_SECONDARY_VARIANT,
        },
    },
    '&:active': {
        backgroundColor: theme.yds.ACTION_SECONDARY_VARIANT,
    },
}));
const AccordionBase = styled.div(({ $isDisabled, theme }) => ({
    borderTop: `2px solid ${$isDisabled ? theme.yds.TEXT_DISABLED : theme.yds.TEXT_DEFAULT}`,
    marginBottom: '-2px',
    borderBottom: `2px solid ${$isDisabled ? theme.yds.TEXT_DISABLED : theme.yds.TEXT_DEFAULT}`,
    backgroundColor: theme.yds.BACKGROUND,
}));
const AccordionContent = styled.div(({ $isDisabled, $isOpen, theme, $padding }) => ({
    display: $isOpen ? 'block' : 'none',
    margin: $padding === 'vertical' || $padding === 'none' ? 0 : '0 16px',
    padding: $padding === 'horizontal' || $padding === 'none' ? 0 : '16px 0',
    color: $isDisabled ? theme.yds.TEXT_DISABLED : theme.yds.TEXT_DEFAULT,
}));
const AccordionList = styled.div({});

const AccordionItem = (_a) => {
    var { id, size = 'md', title, defaultOpen = false, isDisabled = false, children, contentProps = {} } = _a, other = __rest(_a, ["id", "size", "title", "defaultOpen", "isDisabled", "children", "contentProps"]);
    const [open, setOpen] = useState(defaultOpen);
    const itemKey = id ? id.replace(' ', '-') : '';
    return (jsxs(AccordionBase, Object.assign({}, other, { "$isDisabled": isDisabled, children: [jsxs(AccordionTitle, { onClick: () => {
                    setOpen(!open);
                }, "$isDisabled": isDisabled, disabled: isDisabled, "$size": size, "aria-expanded": open, id: itemKey ? itemKey + '-trigger' : undefined, "aria-controls": itemKey ? itemKey + '-content' : undefined, children: [title, open ? jsx(ChevronUp, {}) : jsx(ChevronDown, {})] }), jsx(AccordionContent, Object.assign({ "$isDisabled": isDisabled, "$isOpen": open, "$padding": contentProps.padding, id: itemKey ? itemKey + '-content' : undefined, "aria-labelledby": itemKey ? itemKey + '-trigger' : undefined }, contentProps, { children: children }))] })));
};
const Accordion = (_a) => {
    var { children } = _a, other = __rest(_a, ["children"]);
    return jsx(AccordionList, Object.assign({}, other, { children: children }));
};

const MenuBase = styled.ul `
  position: absolute;
  background-color: ${({ theme }) => theme.yds.BACKGROUND_INTERACTIVE};
  box-shadow: 0px 0px 16px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  padding: ${({ $menuSize }) => ($menuSize === 'md' ? '8px 0' : '4px 0')};
  margin: 0;
  list-style-type: none;
  z-index: 1000;
  transform: translateX(-100%);
  outline: 0;
  -webkit-font-smoothing: antialiased;
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth}px`}
`;

const mergeRefs = (...refs) => {
    return (node) => {
        for (const ref of refs) {
            if (typeof ref === 'function') {
                ref(node);
            }
            else if (ref !== null) {
                ref.current = node;
            }
        }
    };
};

const MenuContext = createContext({ menuSize: 'md' });

const WINDOW_MARGIN = 12;
const getNextItem = (list, current) => {
    if (list === current) {
        return list.firstElementChild;
    }
    else if (current && current.nextElementSibling) {
        return current.nextElementSibling;
    }
    else {
        return list.firstElementChild;
    }
};
const getPreviousItem = (list, current) => {
    if (list === current) {
        return list.lastElementChild;
    }
    else if (current && current.previousElementSibling) {
        return current.previousElementSibling;
    }
    else {
        return list.lastElementChild;
    }
};
const navigate = (list, current, navigateFn) => {
    let didWrap = false;
    let next = navigateFn(list, current);
    while (next !== null) {
        if (next === list.firstElementChild) {
            if (didWrap) {
                return;
            }
            didWrap = true;
        }
        if (next.hasAttribute('tabindex')) {
            next.focus();
            return;
        }
        else {
            next = navigateFn(list, next);
        }
    }
};
const MenuList = forwardRef(({ id, close, navigateAway, buttonRef, rootElement, autofocus, children }, ref) => {
    const menu = useContext(MenuContext);
    const menuListRef = useRef(null);
    const mergedRef = mergeRefs(menuListRef, ref);
    useLayoutEffect(() => {
        const elem = menuListRef.current;
        if (elem === null) {
            return;
        }
        const container = rootElement.ownerDocument.defaultView;
        if (container === null) {
            return;
        }
        const setPosition = (top, left) => {
            elem.style.top = `${top}px`;
            elem.style.left = `${left}px`;
        };
        const top = rootElement.offsetTop + rootElement.offsetHeight;
        const left = rootElement.offsetLeft + rootElement.offsetWidth;
        setPosition(top, left);
        const elemRect = elem.getBoundingClientRect();
        const bottomMax = container.innerHeight - WINDOW_MARGIN;
        const leftMin = WINDOW_MARGIN;
        const bottomOverflow = Math.max(0, elemRect.bottom - bottomMax);
        const leftUnderflow = Math.max(0, leftMin - elemRect.left);
        setPosition(top - bottomOverflow + 5, left + leftUnderflow);
        if (autofocus) {
            const firstChild = elem.firstElementChild;
            if (firstChild && firstChild instanceof HTMLElement) {
                firstChild.focus();
            }
        }
    }, [rootElement, autofocus]);
    const handleBlur = useCallback((e) => {
        const currentTarget = e.currentTarget;
        requestAnimationFrame(() => {
            // Check if the new focused element is a child of the menu container
            if (buttonRef.current &&
                !buttonRef.current.contains(document.activeElement) &&
                !currentTarget.contains(document.activeElement)) {
                navigateAway();
            }
        });
    }, [navigateAway]);
    const onKeyDown = (e) => {
        const list = menuListRef.current;
        if (list === null) {
            return;
        }
        const current = list.ownerDocument.activeElement;
        const { key } = e;
        if (key === 'ArrowDown') {
            e.preventDefault();
            navigate(list, current, getNextItem);
        }
        else if (key === 'ArrowUp') {
            e.preventDefault();
            navigate(list, current, getPreviousItem);
        }
        else if (key === 'Escape') {
            e.preventDefault();
            rootElement.focus();
            close();
        }
    };
    return (jsx(MenuBase, { id: id, onKeyDown: onKeyDown, ref: mergedRef, "$minWidth": Math.max(220, rootElement.offsetWidth), tabIndex: -1, role: "menu", "$menuSize": menu.menuSize, onBlur: handleBlur, children: children }));
});
const Menu = forwardRef(({ id, rootElement, autofocus, close, buttonRef, navigateAway, children }, ref) => {
    if (rootElement) {
        return (jsx(Fragment, { children: jsx(MenuList, { ref: ref, id: id, close: close, rootElement: rootElement, buttonRef: buttonRef, navigateAway: navigateAway, autofocus: autofocus, children: children }) }));
    }
    return null;
});

const ButtonBase = styled.button `
  display: inline-flex;
  position: relative;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
  font-weight: 700;
  outline: none;
  line-height: 1.5;
  font-family: '${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif;
  text-decoration: none;
  text-align: center;
  padding: 4px 2px;
  min-height: 32px;
  border: 2px solid transparent;
  cursor: pointer;
  -webkit-font-smoothing: antialiased;

  ${(props) => props.$size === 'xs' &&
    css `
      padding-left: 10px;
      padding-right: 10px;
      min-height: 32px;
      min-width: 32px;
      font-size: 12px;
    `}
  ${(props) => props.$size === 'sm' &&
    css `
      padding-left: 14px;
      padding-right: 14px;
      min-height: 40px;
      min-width: 40px;
      font-size: 14px;
    `}
  ${(props) => props.$size === 'md' &&
    css `
      padding-left: 16px;
      padding-right: 16px;
      min-height: 48px;
      min-width: 48px;
      font-size: 16px;
    `}
  ${(props) => props.$size === 'lg' &&
    css `
      padding-left: 24px;
      padding-right: 24px;
      min-height: 56px;
      min-width: 56px;
      font-size: 18px;
    `}

  ${(props) => props.$iconOnly &&
    css `
      padding-left: 4px;
      padding-right: 4px;
    `}
  &:focus-visible {
    outline: 3px solid ${(props) => props.theme.yds.BORDER_PRIMARY_FOCUS};
    outline-offset: 2px;
  }
  ${(props) => props.$isDisabled &&
    css `
      cursor: not-allowed;
    `}
`;
const ButtonPrimary = styled(ButtonBase) `
  background-color: ${(props) => props.theme.yds.ACTION_PRIMARY};
  border-color: transparent;
  color: ${(props) => props.theme.yds.TEXT_NEGATIVE};

  ${(props) => !(props.$loading || props.$isDisabled) &&
    css `
      &:active {
        background-color: ${(props) => props.theme.yds.ACTION_PRIMARY_VARIANT};
        border-color: transparent;
      }
      @media (hover: hover) {
        &:hover {
          background-color: ${(props) => props.theme.yds.ACTION_PRIMARY_VARIANT};
          border-color: transparent;
        }
      }
    `}
  ${(props) => props.$isDisabled &&
    css `
      color: ${(props) => props.theme.yds.TEXT_DISABLED};
      background-color: ${(props) => props.theme.yds.ACTION_DISABLED};
      border-color: transparent;
    `}
`;
const ButtonSecondary = styled(ButtonBase) `
  background-color: transparent;
  border-color: ${(props) => props.theme.yds.ACTION_PRIMARY};
  color: ${(props) => props.theme.yds.TEXT_DEFAULT};

  ${(props) => !(props.$loading || props.$isDisabled) &&
    css `
      &:active {
        background-color: ${(props) => props.theme.yds.ACTION_SECONDARY_VARIANT};
        border-color: ${(props) => props.theme.yds.ACTION_PRIMARY};
      }
      @media (hover: hover) {
        &:hover {
          background-color: ${(props) => props.theme.yds.ACTION_SECONDARY_VARIANT};
          border-color: ${(props) => props.theme.yds.ACTION_PRIMARY};
        }
      }
    `}

  ${(props) => props.$isDisabled &&
    css `
      color: ${(props) => props.theme.yds.TEXT_DISABLED};
      background-color: transparent;
      border-color: ${(props) => props.theme.yds.ACTION_DISABLED};
      & .yds-spinner path {
        stroke: ${(props) => props.theme.yds.TEXT_DISABLED};
      }
    `}
`;
const ButtonText = styled(ButtonBase) `
  background-color: transparent;
  border-color: transparent;
  color: ${(props) => props.theme.yds.TEXT_DEFAULT};

  ${(props) => props.$removePadding &&
    css `
      padding-left: 0;
      padding-right: 0;
      border-radius: 8px;
    `}

  ${(props) => !(props.$loading || props.$isDisabled || props.$removePadding) &&
    css `
      &:active {
        background-color: ${(props) => props.theme.yds.ACTION_SECONDARY_VARIANT};
        border-color: transparent;
      }
      @media (hover: hover) {
        &:hover {
          background-color: ${(props) => props.theme.yds.ACTION_SECONDARY_VARIANT};
          border-color: transparent;
        }
      }
    `}

  ${(props) => props.$isDisabled &&
    css `
      color: ${(props) => props.theme.yds.TEXT_DISABLED};
      background-color: transparent;
      border-color: transparent;
      &:active {
        background: transparent;
        border-color: transparent;
      }
      @media (hover: hover) {
        &:hover {
          background: transparent;
          border-color: transparent;
        }
      }
      & .yds-spinner path {
        stroke: ${(props) => props.theme.yds.TEXT_DISABLED};
      }
    `}
`;
const iconSize = styled.span `
  display: inline-block;
  line-height: 0;
  & svg {
    ${(props) => props.$size === 'xs' &&
    css `
        width: 16px;
        height: 16px;
      `}
    ${(props) => props.$size === 'sm' &&
    css `
        width: 20px;
        height: 20px;
      `}
    ${(props) => props.$size === 'md' &&
    css `
        width: 24px;
        height: 24px;
      `}
    ${(props) => props.$size === 'lg' &&
    css `
        width: 28px;
        height: 28px;
      `}
  }
`;
const LoadingIndicator = styled(iconSize) `
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const IconWrapper$1 = styled(iconSize) `
  visibility: ${(props) => (props.$loading ? 'hidden' : 'visible')};
`;
const TextWrapper = styled.span `
  display: ${(props) => (props.$iconOnly ? 'none' : 'block')};
  visibility: ${(props) => (props.$loading ? 'hidden' : 'visible')};
  padding-left: ${(props) => (props.$size === 'xs' && !props.$hasIcon ? '4px' : '8px')};
  padding-right: ${(props) => (props.$size === 'xs' && !props.$hasIconAfter ? '4px' : '8px')};
  ${(props) => props.$variant === 'text' &&
    props.$removePadding &&
    !props.$hasIcon &&
    css `
      padding-left: 0;
    `}
  ${(props) => props.$variant === 'text' &&
    props.$removePadding &&
    !props.$hasIconAfter &&
    css `
      padding-right: 0;
    `}
`;

const SpinnerWrapper = styled.div `
  display: inline-flex;
  align-items: center;
  flex-direction: column;
  color: ${({ $variant, theme }) => $variant === 'default-negative' || $variant === 'highlight-negative'
    ? theme.yds.TEXT_NEGATIVE
    : theme.yds.TEXT_DEFAULT};
`;
const rotate = keyframes `
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
`;
const SpinnerBase = styled.svg `
  animation: ${rotate} linear infinite;
  animation-duration: ${({ $size }) => ($size === 'lg' ? '1000ms' : '720ms')};
  width: ${({ $size }) => ($size === 'lg' ? '72px' : $size === 'sm' ? '24px' : '48px')};
`;
const SpinnerBackground = styled.circle `
  stroke: ${({ $variant, $colors, theme }) => ($colors === null || $colors === void 0 ? void 0 : $colors.background)
    ? undefined
    : $variant === 'default-negative' || $variant === 'highlight-negative'
        ? theme.yds.SPINNER_BACKGROUND_NEGATIVE
        : theme.yds.SPINNER_BACKGROUND_DEFAULT};
`;
const SpinnerWheel = styled.path `
  stroke: ${({ $variant, $colors, theme }) => ($colors === null || $colors === void 0 ? void 0 : $colors.spinner)
    ? undefined
    : $variant === 'default-negative'
        ? theme.yds.SPINNER_NEGATIVE
        : $variant === 'highlight-negative'
            ? theme.yds.SPINNER_HIGHLIGHT_NEGATIVE
            : $variant === 'highlight'
                ? theme.yds.SPINNER_HIGHLIGHT_DEFAULT
                : theme.yds.SPINNER_DEFAULT};
`;
const SpinnerLabel = styled.div `
  font-size: 14px;
  line-height: 1.5;
  margin-top: 4px;
`;

const Spinner = (_a) => {
    var { size = 'md', variant = 'default', hasBackground = false, label, colors } = _a, other = __rest(_a, ["size", "variant", "hasBackground", "label", "colors"]);
    return (jsxs(SpinnerWrapper, Object.assign({ className: "yds-spinner-wrapper", "$variant": variant }, other, { children: [jsxs(SpinnerBase, { className: "yds-spinner", viewBox: "0 0 72 72", "$size": size, children: [hasBackground && (jsx(SpinnerBackground, { cx: "36", cy: "36", r: "30", stroke: (colors === null || colors === void 0 ? void 0 : colors.background) ? colors.background : 'transparent', strokeWidth: "6", fill: "none", "$variant": variant, "$colors": colors })), jsx(SpinnerWheel, { d: "M66 36C66 41.9334 64.2405 47.7336 60.9441 52.6671C57.6476 57.6006 52.9623 61.4458 47.4805 63.7164C41.9987 65.987 35.9667 66.5811 30.1473 65.4236C24.3279 64.266 18.9824 61.4088 14.7868 57.2132C10.5912 53.0176 7.734 47.6721 6.57644 41.8527C5.41888 36.0333 6.01299 30.0013 8.28361 24.5195C10.5542 19.0377 14.3994 14.3524 19.3329 11.0559C24.2664 7.75947 30.0666 6 36 6", stroke: (colors === null || colors === void 0 ? void 0 : colors.spinner) ? colors.spinner : 'currentColor', strokeWidth: "6", strokeLinecap: "round", fill: "none", "$variant": variant, "$colors": colors })] }), label && jsx(SpinnerLabel, { children: label })] })));
};

const buttonTypes = {
    primary: ButtonPrimary,
    secondary: ButtonSecondary,
    text: ButtonText,
};
const Button = forwardRef((_a, ref) => {
    var { variant = 'primary', isDisabled = false, iconBefore = undefined, iconAfter = undefined, iconOnly = false, loading = false, size = 'md', text, accessibleText, children, fullWidth = false, removePadding = false } = _a, other = __rest(_a, ["variant", "isDisabled", "iconBefore", "iconAfter", "iconOnly", "loading", "size", "text", "accessibleText", "children", "fullWidth", "removePadding"]);
    const StyledButton = buttonTypes[variant];
    return (jsxs(StyledButton, Object.assign({ "$size": size, "$fullWidth": fullWidth, "$iconOnly": (iconBefore && !iconAfter) || (!iconBefore && iconAfter) ? iconOnly : false, "aria-label": accessibleText ? accessibleText : text, "$isDisabled": isDisabled, "aria-disabled": isDisabled || loading, "$loading": loading, "$removePadding": removePadding, ref: ref }, other, { children: [!!iconBefore && (jsx(IconWrapper$1, { "$size": size, "$loading": loading, className: "yds-button__icon", children: iconBefore })), children !== undefined && (jsx(TextWrapper, { "$hasIcon": !!iconBefore, "$hasIconAfter": !!iconAfter, "$loading": loading, "$isDisabled": isDisabled, "$size": size, "$variant": variant, "$removePadding": removePadding, className: "yds-button__content", children: children })), text && text !== '' && (jsx(TextWrapper, { "$iconOnly": iconOnly && !!iconBefore, "$hasIcon": !!iconBefore, "$hasIconAfter": !!iconAfter, "$loading": loading, "$isDisabled": isDisabled, "$size": size, "$variant": variant, "$removePadding": removePadding, className: "yds-button__text", children: text })), !!iconAfter && (jsx(IconWrapper$1, { "$size": size, "$loading": loading, className: "yds-button__icon", children: iconAfter })), loading && (jsx(LoadingIndicator, { "$size": size, children: jsx(Spinner, { variant: variant === 'primary' ? 'default-negative' : 'default' }) }))] })));
});

const ActionMenu = (_a) => {
    var { text, id, menuId, children, variant = 'text', dropdown = true, fullWidth = false, openMenuOnClick = true, onClose, onOpen, menuSize = 'md' } = _a, buttonProps = __rest(_a, ["text", "id", "menuId", "children", "variant", "dropdown", "fullWidth", "openMenuOnClick", "onClose", "onOpen", "menuSize"]);
    const [menu, setMenu] = useState(null);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);
    const closeMenu = useCallback(() => {
        var _a;
        setMenu(null);
        (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        if (onClose) {
            onClose();
        }
    }, [onClose]);
    const navigateAway = useCallback(() => {
        setMenu(null);
        if (onClose) {
            onClose();
        }
    }, [onClose]);
    const openMenu = useCallback((e) => {
        if (openMenuOnClick) {
            const buttonElement = e.currentTarget;
            setMenu((menu) => (menu ? null : { root: buttonElement, autofocus: false }));
        }
        if (onOpen) {
            onOpen();
        }
    }, [onOpen, openMenuOnClick]);
    const handleBlur = useCallback(() => {
        // Give browser time to focus the next element
        requestAnimationFrame(() => {
            // Check if the new focused element is either menu item or the menu button
            if (menu &&
                buttonRef.current &&
                !buttonRef.current.contains(document.activeElement) &&
                menuRef.current &&
                !menuRef.current.contains(document.activeElement)) {
                navigateAway();
            }
        });
    }, [onClose]);
    return (jsxs(MenuContext.Provider, { value: { closeMenu: closeMenu, menuSize: menuSize }, children: [jsx(Button, Object.assign({ ref: buttonRef, variant: variant, iconAfter: dropdown ? jsx(ChevronDown, {}) : undefined, text: text, id: id, fullWidth: fullWidth, removePadding: true }, buttonProps, { onClick: openMenu, onKeyDown: (e) => {
                    if (menu && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        setMenu(null);
                    }
                    else if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setMenu({ root: e.currentTarget, autofocus: true });
                        if (menu && menuRef.current) {
                            const firstChild = menuRef.current.firstElementChild;
                            if (firstChild && firstChild instanceof HTMLElement) {
                                firstChild.focus();
                            }
                        }
                    }
                }, onBlur: handleBlur, "aria-haspopup": "true", "aria-controls": menu ? menuId : undefined, "aria-expanded": menu ? 'true' : undefined })), jsx(Menu, { ref: menuRef, buttonRef: buttonRef, id: menuId, close: closeMenu, navigateAway: navigateAway, rootElement: menu && menu.root, autofocus: (menu === null || menu === void 0 ? void 0 : menu.autofocus) || false, children: children })] }));
};

const ThemeContext = createContext({
    unit: 'rem',
    baseUnit: 16,
});

function useThemeContext() {
    return useContext(ThemeContext);
}

const Unit = (unit) => {
    const context = useThemeContext();
    const isPixels = context.unit === 'px';
    const value = Number(String(unit).replace(/[a-z]*$/, ''));
    return isPixels ? value : `${value / context.baseUnit}${context === null || context === void 0 ? void 0 : context.unit}`;
};

const getFontSize = (size, desktop) => {
    switch (size) {
        case 'lg':
            return desktop
                ? Unit(typography.YLE_SANS_4XL_BOLD_FONT_SIZE)
                : Unit(typography.YLE_SANS_2XL_BOLD_FONT_SIZE);
        case 'md':
            return desktop
                ? Unit(typography.YLE_SANS_2XL_BOLD_FONT_SIZE)
                : Unit(typography.YLE_SANS_XL_BOLD_FONT_SIZE);
        case 'sm':
            return desktop
                ? Unit(typography.YLE_SANS_L_BOLD_FONT_SIZE)
                : Unit(typography.YLE_SANS_M_BOLD_FONT_SIZE);
        case 'xs':
            return desktop
                ? Unit(typography.YLE_SANS_M_BOLD_FONT_SIZE)
                : Unit(typography.YLE_SANS_S_BOLD_FONT_SIZE);
        default:
            return undefined;
    }
};
const getLineHeight = (size, desktop) => {
    switch (size) {
        case 'lg':
            return desktop
                ? typography.YLE_SANS_4XL_BOLD_LINE_HEIGHT
                : typography.YLE_SANS_2XL_BOLD_LINE_HEIGHT;
        case 'md':
            return desktop
                ? typography.YLE_SANS_2XL_BOLD_LINE_HEIGHT
                : typography.YLE_SANS_XL_BOLD_LINE_HEIGHT;
        case 'sm':
            return desktop
                ? typography.YLE_SANS_L_BOLD_LINE_HEIGHT
                : typography.YLE_SANS_M_BOLD_LINE_HEIGHT;
        case 'xs':
            return desktop
                ? typography.YLE_SANS_M_BOLD_LINE_HEIGHT
                : typography.YLE_SANS_S_BOLD_LINE_HEIGHT;
        default:
            return undefined;
    }
};
const getMargin = (size, desktop) => {
    switch (size) {
        case 'lg':
            return desktop ? Unit(24) : Unit(16);
        case 'md':
            return desktop ? Unit(24) : Unit(16);
        case 'sm':
        case 'xs':
            return desktop ? Unit(16) : Unit(16);
        default:
            return undefined;
    }
};
const StyledArticleHading = styled.h1(({ theme, $weight, $size, $noMargin }) => ({
    fontFamily: typographyTokens.FONT_FAMILY_YLE,
    lineHeight: getLineHeight($size, false),
    fontSize: getFontSize($size, false),
    fontWeight: $weight === 'bold' ? 700 : 900,
    color: theme.yds.TEXT_DEFAULT,
    margin: 0,
    marginBottom: $noMargin ? undefined : getMargin($size, false),
    hyphens: 'manual',
    '@media(min-width: 768px)': {
        fontSize: getFontSize($size, true),
        lineHeight: getLineHeight($size, true),
        marginBottom: $noMargin ? undefined : getMargin($size, true),
    },
}));

const ArticleHeading = forwardRef((_a, ref) => {
    var { size, weight, noMargin } = _a, rest = __rest(_a, ["size", "weight", "noMargin"]);
    return (jsx(StyledArticleHading, Object.assign({ ref: ref, "$size": size, "$weight": weight, "$noMargin": noMargin }, rest)));
});

const CheckboxBase = styled.input(({ theme, $isError, $variant }) => css `
    position: absolute;
    appearance: none;
    background-color: ${theme.yds.BACKGROUND};
    border: 2px solid ${$isError ? theme.yds.FEEDBACK_ERROR : theme.yds.ACTION_PRIMARY};
    border-radius: 4px;
    width: 24px;
    height: 24px;
    cursor: pointer;
    margin: 0;
    vertical-align: bottom;
    margin-right: 12px;
    margin-left: -36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &[aria-disabled='true'] {
      cursor: not-allowed;
      background-color: transparent;
      border-color: ${theme.yds.BORDER_DISABLED};
    }
    &:focus,
    &.focus {
      outline: 3px solid ${theme.yds.BORDER_PRIMARY_FOCUS};
      outline-offset: 2px;
    }
    @supports selector(:focus-visible) {
      &:focus {
        outline: none;
      }
      &:focus-visible {
        outline: 3px solid ${theme.yds.BORDER_PRIMARY_FOCUS};
        outline-offset: 2px;
      }
    }
    &[aria-disabled='true']:checked {
      background-color: transparent;
      border-color: ${theme.yds.BORDER_DISABLED};
    }
    &:checked::after {
      content: '';
      background-color: ${$isError
    ? theme.yds.FEEDBACK_ERROR
    : $variant === 'primary'
        ? theme.yds.ACTION_PRIMARY
        : theme.yds.FEEDBACK_HIGHLIGHT};
      -webkit-mask-image: url('data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M14.158%204.825a1.167%201.167%200%200%200-1.65-1.65L6%209.683%203.492%207.175a1.167%201.167%200%201%200-1.65%201.65l3.333%203.333a1.167%201.167%200%200%200%201.65%200l7.333-7.333Z%22%20fill%3D%22%23131415%22%2F%3E%3C%2Fsvg%3E');
      -webkit-mask-size: 100% 100%;
      -webkit-mask-repeat: no-repeat;
      mask-image: url('data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M14.158%204.825a1.167%201.167%200%200%200-1.65-1.65L6%209.683%203.492%207.175a1.167%201.167%200%201%200-1.65%201.65l3.333%203.333a1.167%201.167%200%200%200%201.65%200l7.333-7.333Z%22%20fill%3D%22%23131415%22%2F%3E%3C%2Fsvg%3E');
      mask-size: 100% 100%;
      mask-repeat: no-repeat;
      width: 16px;
      height: 16px;
      display: inline-block;
      vertical-align: top;
    }
    &:indeterminate::after {
      content: '';
      background-color: ${$isError
    ? theme.yds.FEEDBACK_ERROR
    : $variant === 'primary'
        ? theme.yds.ACTION_PRIMARY
        : theme.yds.FEEDBACK_HIGHLIGHT};
      -webkit-mask-image: url('data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M2%208.15C2%207.515%202.515%207%203.15%207h9.7a1.15%201.15%200%200%201%200%202.3h-9.7A1.15%201.15%200%200%201%202%208.15Z%22%20fill%3D%22%23131415%22%2F%3E%3C%2Fsvg%3E');
      -webkit-mask-size: 100% 100%;
      -webkit-mask-repeat: no-repeat;
      mask-image: url('data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M2%208.15C2%207.515%202.515%207%203.15%207h9.7a1.15%201.15%200%200%201%200%202.3h-9.7A1.15%201.15%200%200%201%202%208.15Z%22%20fill%3D%22%23131415%22%2F%3E%3C%2Fsvg%3E');
      mask-size: 100% 100%;
      mask-repeat: no-repeat;
      width: 16px;
      height: 16px;
      display: inline-block;
    }

    &[aria-disabled='true']:checked::after,
    &[aria-disabled='true']:indeterminate::after {
      background-color: ${theme.yds.TEXT_DISABLED};
    }
  `);

const CheckboxGroupContext = createContext(undefined);

function useCheckboxGroup() {
    return useContext(CheckboxGroupContext);
}

const CheckableLabel = styled.label(({ theme, $isDisabled, $hideLabel, $useUnderlay }) => ({
    display: 'inline-block',
    minHeight: 24,
    height: $hideLabel ? 24 : 'auto',
    width: $hideLabel ? 24 : 'auto',
    lineHeight: 1.5,
    clipPath: $hideLabel ? 'inset(-5px calc(100% - 29px) -5px -5px)' : 'none',
    fontFamily: `'${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif`,
    cursor: 'pointer',
    color: $isDisabled ? theme.yds.TEXT_DISABLED : theme.yds.ACTION_PRIMARY,
    paddingLeft: $hideLabel ? 24 : 36,
    textIndent: $hideLabel ? 12 : 0,
    '&:after': $useUnderlay
        ? {
            position: 'absolute',
            width: '100%',
            height: '100%',
            inset: 0,
            content: `''`,
        }
        : {},
}));

const Checkbox = forwardRef((_a, ref) => {
    var { isDisabled = false, label: labelProp, variant = 'primary', error = false, name: nameProp, value: valueProp, hideLabel = false, useUnderlay = false, onChange: onChangeProp } = _a, other = __rest(_a, ["isDisabled", "label", "variant", "error", "name", "value", "hideLabel", "useUnderlay", "onChange"]);
    const checkboxGroup = useCheckboxGroup();
    let onChange = onChangeProp;
    let checked = other.checked;
    let name = nameProp;
    const value = valueProp
        ? valueProp
        : typeof labelProp === 'string'
            ? labelProp
            : '';
    const label = labelProp;
    if (checkboxGroup) {
        if (typeof onChangeProp === 'undefined') {
            onChange = checkboxGroup.onChange;
        }
        const checkboxValues = checkboxGroup.value || [];
        if (checkboxValues.length && checkboxValues.includes(value)) {
            checked = true;
        }
        else {
            checked = false;
        }
        name = checkboxGroup.name;
    }
    return (jsxs(CheckableLabel, { "$isDisabled": isDisabled, "$isError": error, "$hideLabel": hideLabel, "$useUnderlay": useUnderlay, children: [jsx(CheckboxBase, Object.assign({ ref: ref, type: "checkbox", "$variant": variant, "$isError": error, name: name, value: value, checked: checked, "aria-disabled": isDisabled, onClick: isDisabled
                    ? (e) => e.preventDefault()
                    : () => {
                        return true;
                    }, onChange: onChange }, other)), label] }));
});

const Message = styled.p(({ theme, $error }) => ({
    color: $error ? theme.yds.FEEDBACK_ERROR : theme.yds.TEXT_DEFAULT,
    fontFamily: `'${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif`,
    textDecoration: 'none',
    fontSize: 14,
    lineHeight: 1.5,
    margin: '8px 0 0 0',
}));

const StyledFieldset$1 = styled.fieldset({
    border: 0,
    padding: 0,
    margin: 0,
});
const StyledCheckboxGroup = styled.div({
    display: 'flex',
    flexDirection: 'column',
    '& > label:not(:last-child)': {
        marginBottom: 8,
    },
    [`&:not(:only-child)`]: {
        marginTop: 8,
    },
});
const StyledLabel$1 = styled.legend(({ theme }) => ({
    color: theme.yds.TEXT_DEFAULT,
    marginBottom: 8,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5,
    margin: 0,
    padding: 0,
}));
const StyledDescription$1 = styled.div(({ theme }) => ({
    color: theme.yds.TEXT_DEFAULT,
    fontSize: 14,
    lineHeight: 1.5,
}));

const CheckboxGroup = forwardRef((props, ref) => {
    const { children, name, selectedValue: valueProp, id: idProp, errorMessage, label, description, onValueChange } = props, rest = __rest(props, ["children", "name", "selectedValue", "id", "errorMessage", "label", "description", "onValueChange"]);
    const [value, setValueState] = useState(valueProp || []);
    const useFieldset = label || description;
    const hasError = Boolean(errorMessage);
    const id = idProp || `group-${name}`;
    useEffect(() => {
        setValueState(valueProp);
    }, [valueProp]);
    useEffect(() => {
        onValueChange(value);
    }, [value, onValueChange]);
    const handleChange = (e) => {
        if (value.includes(e.currentTarget.value)) {
            setValueState(value.filter((existingValue) => existingValue !== e.currentTarget.value));
        }
        else {
            setValueState(value.concat([e.currentTarget.value]));
        }
    };
    const renderCheckboxGroup = () => (jsx(StyledCheckboxGroup, { "aria-label": label, ref: ref, children: children }));
    return (jsx(CheckboxGroupContext.Provider, { value: {
            name,
            onChange: handleChange,
            value,
            error: hasError,
            errorId: hasError ? `${id}-error` : undefined,
            descId: description ? `${id}-desc` : undefined,
        }, children: useFieldset ? (jsxs(StyledFieldset$1, Object.assign({}, rest, { children: [label && jsx(StyledLabel$1, { children: label }), description && jsx(StyledDescription$1, { id: `${id}-desc`, children: description }), renderCheckboxGroup(), hasError && (jsx(Message, { id: `${id}-error`, "$error": true, children: errorMessage }))] }))) : (jsxs(Fragment, { children: [renderCheckboxGroup(), hasError && (jsx(Message, { id: `${id}-error`, "$error": true, children: errorMessage }))] })) }));
});

const getStatusColor = ({ theme, status }) => {
    switch (status) {
        case 'success':
            return theme.FEEDBACK_SUCCESS;
        case 'error':
            return theme.FEEDBACK_ERROR;
        default:
            return theme.TEXT_DEFAULT;
    }
};
const ChoiceButtonContainer = styled.div(({ theme, $status }) => ({
    overflow: 'hidden',
    fontFamily: `'${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif`,
    fontWeight: 400,
    fontSize: 16,
    backgroundColor: theme.yds.BACKGROUND_INTERACTIVE,
    borderRadius: 8,
    position: 'relative',
    /* HACK: Using the after pseudoelement to draw borders inside the
     * container. This would simpler with outline: -2px, but safari does
     * not support rounded corners for outline. If the issue has been fixed
     * in safari, you could consider using outline instead.
     * Issue: https://bugs.webkit.org/show_bug.cgi?id=20807 */
    '&::after': $status
        ? {
            borderRadius: 8,
            boxShadow: 'inset 0px 0px 0px 2px ' + getStatusColor({ theme: theme.yds, status: $status }),
            content: '""',
            display: 'block',
            height: '100%',
            width: '100%',
            position: 'absolute',
            top: 0,
            pointerEvents: 'none',
        }
        : {},
    '&:focus-within': {
        outline: '3px solid',
        outlineColor: theme.yds.BORDER_PRIMARY_FOCUS,
    },
    outlineOffset: 2,
}));
const ChoiceButtonHeader = styled.div({
    display: 'flex',
    alignItems: 'center',
    margin: '12px 16px',
});
const ChoiceButtonIconContainer = styled.div(({ theme, $status }) => ({
    marginLeft: 'auto',
    color: getStatusColor({ theme: theme.yds, status: $status }),
    '> svg': {
        verticalAlign: 'middle',
    },
}));

const ChoiceContainer = ({ status = undefined, icon, content, children, }) => {
    return (jsxs(ChoiceButtonContainer, { "$status": status, children: [jsxs(ChoiceButtonHeader, { children: [children, jsx(ChoiceButtonIconContainer, { "$status": status, children: icon })] }), jsx("div", { children: content })] }));
};

const RadioGroupContext = createContext(undefined);

function useRadioGroup() {
    return useContext(RadioGroupContext);
}

const RadioBase = styled.input(({ theme, $variant, $error }) => css `
    appearance: none;
    background-color: ${theme.yds.BACKGROUND};
    border: 2px solid ${theme.yds.ACTION_PRIMARY};
    border-radius: 50%;
    width: 24px;
    height: 24px;
    cursor: pointer;
    margin: 0;
    margin-right: 12px;
    margin-left: -36px;
    vertical-align: bottom;
    position: relative;

    &:disabled {
      cursor: not-allowed;
      background-color: transparent;
      border-color: ${theme.yds.BACKGROUND_CONTROL_DISABLED};
    }

    &:focus,
    &.focus {
      outline: 3px solid ${theme.yds.FEEDBACK_HIGHLIGHT};
      outline-offset: 2px;
    }

    @supports selector(:focus-visible) {
      &:focus {
        outline: none;
      }
      &:focus-visible {
        outline: 3px solid ${theme.yds.FEEDBACK_HIGHLIGHT};
        outline-offset: 2px;
      }
    }

    &::after {
      content: '';
      background-color: ${$variant === 'secondary'
    ? theme.yds.FEEDBACK_HIGHLIGHT
    : theme.yds.ACTION_PRIMARY};
      border-radius: 50%;
      width: 0;
      height: 0;
      opacity: 0;
      display: inline-block;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      position: absolute;
      transition: all 150ms ease-in;
    }

    &:checked::after {
      width: 12px;
      height: 12px;
      opacity: 1;
    }

    &:checked:disabled {
      border-color: ${theme.yds.TEXT_DISABLED};

      &::after {
        background-color: ${theme.yds.TEXT_DISABLED};
      }
    }

    ${$error &&
    css `
      border-color: ${theme.yds.FEEDBACK_ERROR};

      &::after {
        background-color: ${theme.yds.FEEDBACK_ERROR};
      }
    `}
  `);

const Radio = forwardRef((_a, ref) => {
    var { variant = 'primary', isDisabled = false, error: errorProp, label, name: nameProp, value, checked: checkedProp, onChange: onChangeProp, hideLabel = false, useUnderlay = false } = _a, rest = __rest(_a, ["variant", "isDisabled", "error", "label", "name", "value", "checked", "onChange", "hideLabel", "useUnderlay"]);
    const radioGroup = useRadioGroup();
    let onChange = onChangeProp;
    let checked = checkedProp;
    let name = nameProp;
    let error = errorProp;
    let errorId;
    let descId;
    if (radioGroup) {
        if (typeof checked === 'undefined') {
            checked = radioGroup.value === value;
        }
        if (typeof name === 'undefined') {
            name = radioGroup.name;
        }
        if (typeof onChangeProp === 'undefined') {
            onChange = radioGroup.onChange;
        }
        error = radioGroup.error;
        errorId = radioGroup.errorId;
        descId = radioGroup.descId;
    }
    return (jsxs(CheckableLabel, { "$isDisabled": isDisabled, "$isError": !!error, "$hideLabel": hideLabel, "$useUnderlay": useUnderlay, children: [jsx(RadioBase, Object.assign({ ref: ref, type: "radio", disabled: isDisabled, name: name, "aria-disabled": isDisabled, "aria-describedby": [descId, errorId].filter(Boolean).join(' ') || undefined, "$variant": variant, "$error": error, checked: checked, value: value, onChange: onChange }, rest)), label] }));
});

const ChoiceButtonRadio = styled(Radio)({
    '&:focus-visible': {
        outline: 'none',
    },
    '&:focus': {
        outline: 'none',
    },
});

const ChoiceButton = forwardRef((_a, ref) => {
    var { status = undefined, icon, content } = _a, args = __rest(_a, ["status", "icon", "content"]);
    return (jsx(ChoiceContainer, { status: status, content: content, icon: icon, children: jsx(ChoiceButtonRadio, Object.assign({ ref: ref, useUnderlay: true }, args)) }));
});

const ChoiceBoxCheckbox = styled(Checkbox)({
    '&:focus-visible': {
        outline: 'none',
    },
    '&:focus': {
        outline: 'none',
    },
});

const ChoiceBox = forwardRef((_a, ref) => {
    var { status = undefined, icon, content } = _a, args = __rest(_a, ["status", "icon", "content"]);
    return (jsx(ChoiceContainer, { status: status, content: content, icon: icon, children: jsx(ChoiceBoxCheckbox, Object.assign({ ref: ref, useUnderlay: true }, args)) }));
});

const MenuItemBase = styled.li `
  padding: ${({ $menuSize }) => ($menuSize === 'md' ? '12px 16px' : '8px 12px;')};
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed' : 'pointer')};
  font-family: '${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif;
  font-size: ${({ $menuSize }) => ($menuSize === 'md' ? '16px;' : '14px;')};
  line-height: ${({ $menuSize }) => ($menuSize === 'md' ? '24px;' : '21px;')};
  color: ${({ $isDisabled, theme }) => $isDisabled ? theme.yds.TEXT_DISABLED : theme.yds.TEXT_DEFAULT};
  background-color: ${({ $isDisabled, $isSelected, theme }) => !$isDisabled && $isSelected ? theme.yds.ACTION_SECONDARY_VARIANT : 'transparent'};
  display: flex;
  flex-flow: row nowrap;
  @media (hover: hover) {
    &:hover {
      background-color: ${({ $isDisabled, theme }) => $isDisabled ? 'transparent' : theme.yds.ACTION_SECONDARY_VARIANT};
    }
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.yds.FEEDBACK_HIGHLIGHT};
    outline-offset: -2px;
  }
`;
const IconWrapper = styled.span `
  display: inline-block;
  line-height: 0;
  width: ${({ $menuSize }) => ($menuSize === 'md' ? '24px;' : '20px;')};
  height: ${({ $menuSize }) => ($menuSize === 'md' ? '24px;' : '20px;')};
  margin-right: 8px;
  & svg {
    width: ${({ $menuSize }) => ($menuSize === 'md' ? '24px;' : '20px;')};
    height: ${({ $menuSize }) => ($menuSize === 'md' ? '24px;' : '20px;')};
    ${({ $highlight, theme }) => ($highlight ? `color: ${theme.yds.FEEDBACK_HIGHLIGHT};` : '')}
  }
`;
const MenuSeparatorBase = styled.li `
  border-top: 2px solid ${({ theme }) => theme.yds.BORDER_SEPARATOR};
`;

const ComboboxContainer = styled.div `
  position: relative;
  & .yds-spinner {
    width: 24px;
    & path {
      stroke: ${({ theme, $isDisabled }) => $isDisabled ? theme.yds.TEXT_DISABLED : theme.yds.TEXT_DEFAULT}
  }
`;
const ComboboxMenu = styled(MenuBase) `
  transform: none;
  width: 100%;
  margin-top: 5px;
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  max-height: ${({ $menuMaxHeight }) => ($menuMaxHeight ? $menuMaxHeight : 'unset')};
  overflow-y: auto;
  color-scheme: ${({ theme }) => (theme.ydsThemeName === 'dark' ? 'dark' : 'light')};
`;
const ComboboxMenuItem = styled(MenuItemBase) `
  outline: ${({ $isHighlighted, theme }) => $isHighlighted ? '2px solid ' + theme.yds.BORDER_PRIMARY_FOCUS : 'none'};
  outline-offset: -4px;
  padding-left: 12px;
  background-color: transparent;
  &:hover {
    background: unset;
  }
`;

const TextInputFieldset = styled.fieldset `
  border: none;
  margin: 0;
  padding: 0;
`;
const determineInputBorder = (defaultValue) => (props) => {
    if (props.$hasIcon)
        return 'none';
    if (props.$error) {
        return `2px solid ${props.theme.yds.FEEDBACK_ERROR}`;
    }
    else if (props.$success) {
        return `2px solid ${props.theme.yds.FEEDBACK_SUCCESS}`;
    }
    else {
        return `2px solid ${defaultValue}`;
    }
};
const StyledTextInput = styled.input `
  align-items: center;
  background-color: ${({ theme }) => theme.yds.BACKGROUND_INPUT ? theme.yds.BACKGROUND_INPUT : 'transparent'};
  border: ${(props) => determineInputBorder(props.theme.yds.BORDER)};
  border-radius: ${({ $hasIcon }) => ($hasIcon ? '6px' : '8px')};
  color: ${({ theme }) => theme.yds.TEXT_DEFAULT};
  font-family: '${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif;
  font-size: 16px;
  text-decoration: none;
  cursor: text;
  height: ${({ $hasIcon }) => ($hasIcon ? '44px' : '48px')};
  outline: none;
  padding: 16px;
  width: 100%;
  &:focus,
  &:active {
    outline: ${({ theme }) => `3px solid ${theme.yds.BORDER_PRIMARY_FOCUS}`};
    outline-offset: ${({ $hasIcon }) => ($hasIcon ? '4px' : '2px')};
    position: relative;
    z-index: 1;
  }
  &:disabled {
    color: ${({ theme }) => theme.yds.TEXT_DISABLED};
    border: ${({ theme, $hasIcon }) => $hasIcon ? 'none' : `2px solid ${theme.yds.BORDER_DISABLED}`};
    cursor: default;
  }
  &::placeholder {
    color: ${({ theme }) => theme.yds.TEXT_INPUT_PLACEHOLDER};
  }
  &:disabled:focus,
  &:disabled:active {
    color: ${({ theme }) => theme.yds.TEXT_DISABLED};
    box-shadow: none;
    background-color: ${({ theme }) => theme.yds.BACKGROUND_INPUT};
  }
  &:disabled::placeholder {
    color: ${({ theme }) => theme.yds.TEXT_DISABLED};
  }
  padding-left: ${({ $hasIconBefore }) => ($hasIconBefore ? '48px' : '16px')};
  padding-right: ${({ $hasIcon, $hasIconAfter }) => $hasIcon ? ($hasIconAfter ? '80px' : '48px') : '16px'};
`;
const TextInputDescription = styled.p `
  color: ${({ theme }) => theme.yds.TEXT_DEFAULT};
  font-family: '${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif;
  text-decoration: none;
  font-size: 14px;
  line-height: 150%;
  margin: 8px 0 0 0;
`;
const TextInputErrorMessage = styled(TextInputDescription) `
  color: ${({ theme }) => theme.yds.FEEDBACK_ERROR};
`;
const determineInputAndIconContainerBorder = (defaultValue) => (props) => {
    if (props.disabled)
        return `2px solid ${props.theme.yds.BORDER_DISABLED}`;
    else
        return determineInputBorder(defaultValue)(props);
};
const InputAndIconContainer = styled.div `
  display: flex;
  border: ${({ theme }) => determineInputAndIconContainerBorder(theme.yds.BORDER)};
  border-radius: 8px;
  position: relative;
  &:focus-within {
    border: ${({ theme }) => determineInputAndIconContainerBorder(theme.yds.BORDER)};
  }
`;
const iconBase = css `
  align-items: center;
  background-color: transparent;
  border-radius: 4px;
  border: none;
  color: ${({ theme }) => theme.yds.TEXT_DEFAULT};
  display: flex;
  justify-content: center;
  padding: 0;
  flex: 0;
  width: 48px;
  height: 100%;
  border-radius: 12px;
  position: absolute;
  z-index: 2;
  &:focus-visible {
    outline: none;
  }
  &:focus-visible svg {
    border-radius: 2px;
    outline: 3px solid ${({ theme }) => theme.yds.BORDER_PRIMARY_FOCUS};
    outline-offset: 2px;
  }
`;
const InputIconWrapper = styled.span `
  ${iconBase};
  cursor: default;
  color: ${({ theme, $isDisabled }) => $isDisabled ? theme.yds.TEXT_DISABLED : theme.yds.TEXT_DEFAULT};
  ${({ $location }) => $location === 'before'
    ? 'left: 0; padding-left: 8px;'
    : $location === 'after'
        ? 'right: 0; padding-right: 8px;'
        : 'right: 48px; width: 32px; padding-left: 8px;'}
  & > svg {
    height: 24px;
    width: 24px;
  }
`;
const LoadingIndicatorWrapper = styled.span `
  align-items: center;
  background-color: transparent;
  border-radius: 4px;
  border: none;
  color: ${({ theme }) => theme.yds.TEXT_DEFAULT};
  display: flex;
  justify-content: center;
  padding: 0;
  flex: 0;
  width: 48px;
  height: 100%;
  border-radius: 12px;
  position: absolute;
  left: 0;
  padding-left: 8px;
  z-index: 2;
`;
const InputButtonIcon = styled.button `
  ${iconBase};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  color: ${({ theme, $isDisabled }) => $isDisabled ? theme.yds.TEXT_DISABLED : theme.yds.TEXT_DEFAULT};
  ${({ $location }) => $location === 'before'
    ? 'left: 0; padding-left: 8px;'
    : $location === 'after'
        ? 'right: 0; padding-right: 8px;'
        : 'right: 48px; width: 32px; padding-left: 8px;'}
  & > svg {
    height: 24px;
    width: 24px;
  }
`;

const FormElementLabelStyle = styled.label(({ theme, $hasMarginBottom, $isDisabled }) => ({
    display: 'block',
    fontFamily: `"${typography.DEFAULT_M_BOLD_FONT_FAMILY}", "Open Sans", sans-serif`,
    fontWeight: typography.DEFAULT_M_BOLD_FONT_WEIGHT,
    fontSize: typography.DEFAULT_M_BOLD_FONT_SIZE,
    fontStyle: typography.DEFAULT_M_BOLD_FONT_STYLE,
    lineHeight: typography.DEFAULT_M_BOLD_LINE_HEIGHT,
    marginBottom: $hasMarginBottom ? '8px' : '0',
    color: $isDisabled ? theme.yds.TEXT_DISABLED : theme.yds.TEXT_DEFAULT,
    '&[for]': {
        cursor: 'pointer',
    },
    '&.visually-hidden': {
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: '1px',
        overflow: 'hidden',
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: '1px',
    },
}));
const OptionalLabel = styled.span({
    fontWeight: typographyTokens.FONT_WEIGHT_NORMAL,
});

const FormElementLabel = (_a) => {
    var { isDisabled = false, isHidden = false, isRequired = false, hasMarginBottom = true, label = '', optionalLabel, children } = _a, other = __rest(_a, ["isDisabled", "isHidden", "isRequired", "hasMarginBottom", "label", "optionalLabel", "children"]);
    return (jsxs(FormElementLabelStyle, Object.assign({ "$isDisabled": isDisabled, className: isHidden ? 'visually-hidden' : '', "$hasMarginBottom": hasMarginBottom }, other, { children: [label, children, optionalLabel && isRequired === false && jsxs(OptionalLabel, { children: [" ", optionalLabel] })] })));
};

const validateType = (type) => {
    if (!['text', 'email', 'password', 'number', 'tel'].includes(type)) {
        console.error(`Text input only supports prop type = 'text' | 'email' | 'password' | 'number' | 'tel', received ${type}. Using 'text' as default.`);
        return 'text';
    }
    return type;
};
const Icon = ({ icon, location, isDisabled = false }) => {
    var _a;
    if (icon.onClick || ((_a = icon.props) === null || _a === void 0 ? void 0 : _a.onClick)) {
        return (jsx(InputButtonIcon, Object.assign({ "aria-label": icon.ariaLabel, onClick: icon.onClick, type: "button", "$location": location, "$isDisabled": isDisabled, disabled: isDisabled }, icon.props, { children: jsx(icon.componentFn, {}) })));
    }
    return (jsx(InputIconWrapper, { "$location": location, "$isDisabled": isDisabled, children: jsx(icon.componentFn, { "aria-label": icon.ariaLabel }) }));
};
/**
 * A generic input component with a label and optional error and description messages.
 * extra params are passed to the underlying input component as-is, except for `aria-disabled`, which
 * is controlled by `isDisabled` and `aria-labelled-by`, which is generated from `id`. Note that
 * the `props.icon.componentFn` is a function, not a component argument, i.e. `componentFn: Eye` instead of `componentFn: <Eye />`.
 *
 * @param {TextInputProps} props
 * @param {Object} props.icon
 * @param {Function} props.icon.componentFn - A render function of a component, instead of an already rendered component,
 *   i.e. `componentFn: Eye` instead of `componentFn: <Eye />`.
 * */
const TextInput = forwardRef((_a, ref) => {
    var { label, labelOptions = {}, id, value, onChange, placeholder, description, icon, iconBefore, iconClear, errorMessage, autocomplete, type = 'text', success = false, isRequired = false, isDisabled = false, showLoadingIndicator = false, children } = _a, other = __rest(_a, ["label", "labelOptions", "id", "value", "onChange", "placeholder", "description", "icon", "iconBefore", "iconClear", "errorMessage", "autocomplete", "type", "success", "isRequired", "isDisabled", "showLoadingIndicator", "children"]);
    const inputRef = ref || useRef(null);
    const StyledInput = (jsx(StyledTextInput, Object.assign({ id: id, ref: inputRef, value: value, onChange: onChange, placeholder: placeholder, "$error": Boolean(errorMessage), autoComplete: autocomplete, type: validateType(type), "$success": !errorMessage && success, disabled: isDisabled, required: isRequired, "aria-disabled": isDisabled, "aria-labelledby": `${id}-label`, "$hasIcon": Boolean(icon) || showLoadingIndicator, "$hasIconBefore": Boolean(iconBefore) || showLoadingIndicator, "$hasIconAfter": Boolean(iconClear) }, other)));
    return (jsxs(TextInputFieldset, { children: [jsx(FormElementLabel, Object.assign({ htmlFor: id, id: `${id}-label`, isDisabled: isDisabled, isRequired: isRequired }, labelOptions, { children: label })), icon || iconBefore || showLoadingIndicator ? (jsxs(InputAndIconContainer, { disabled: isDisabled, "$success": !errorMessage && success, "$error": Boolean(errorMessage), children: [iconBefore && !showLoadingIndicator && (jsx(Icon, { icon: iconBefore, location: 'before', isDisabled: isDisabled })), showLoadingIndicator && (jsx(LoadingIndicatorWrapper, { children: jsx(Spinner, { size: "sm", variant: "default" }) })), StyledInput, iconClear && (jsx(Icon, { icon: iconClear, location: icon ? 'middle' : 'after', isDisabled: isDisabled })), icon && jsx(Icon, Object.assign({ icon: icon, location: 'after', isDisabled: isDisabled }, icon))] })) : (StyledInput), children, Boolean(errorMessage) && jsx(TextInputErrorMessage, { children: errorMessage }), Boolean(description) && jsx(TextInputDescription, { children: description })] }));
});

const DefaultComboboxMenuItem = (_a) => {
    var { item, index, highlightedIndex, selectedItem, itemProps, children, menuSize } = _a, other = __rest(_a, ["item", "index", "highlightedIndex", "selectedItem", "itemProps", "children", "menuSize"]);
    return (jsxs(ComboboxMenuItem, Object.assign({ "$isHighlighted": highlightedIndex === index, "$isSelected": item.value === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value), "$menuSize": menuSize, "$isDisabled": false }, other, itemProps, { children: [jsx(IconWrapper, { "$menuSize": "md", "$highlight": true, children: item.value === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value) && jsx(Check, {}) }), children] }), `${item}${index}`));
};
const ComboboxSingleSelect = forwardRef((_a, ref) => {
    var { label, labelOptions = {}, value, placeholder, description, icon, iconBefore, loading = false, loadingText = 'Ladataan...', errorMessage, autocomplete, type = 'text', success = false, isDisabled = false, menuSize = 'md', menuMaxHeight = '300px', items = [], controlledItems = null, setControlledItems = null, menuItemComponent = null, handleInputValueChange, handleItemToString, handleSelectedItemChange, controlledValue, handleControlledValueChange, clearSelectionOnEmptyInput = true, showAllValuesOnOpen = false, noResultsText = 'Ei tuloksia', toggleButtonText = 'Näytä vaihtoehdot', clearText = 'Tyhjennä', searchIconLabel = 'Haku' } = _a, other = __rest(_a, ["label", "labelOptions", "value", "placeholder", "description", "icon", "iconBefore", "loading", "loadingText", "errorMessage", "autocomplete", "type", "success", "isDisabled", "menuSize", "menuMaxHeight", "items", "controlledItems", "setControlledItems", "menuItemComponent", "handleInputValueChange", "handleItemToString", "handleSelectedItemChange", "controlledValue", "handleControlledValueChange", "clearSelectionOnEmptyInput", "showAllValuesOnOpen", "noResultsText", "toggleButtonText", "clearText", "searchIconLabel"]);
    const isItemDisabled = (item) => item.isDisabled ? item.isDisabled : false;
    const comboboxItems = controlledItems ? controlledItems : items ? items : [];
    const [inputItems, setInputItems] = useState(items);
    const comboboxControlledItems = controlledItems ? controlledItems : inputItems;
    const setComboboxControlledItems = setControlledItems ? setControlledItems : setInputItems;
    const { isOpen, getToggleButtonProps, getLabelProps, getMenuProps, getInputProps, highlightedIndex, selectedItem, inputValue, selectItem, setInputValue, getItemProps, } = useCombobox({
        items: comboboxItems,
        isItemDisabled,
        onInputValueChange: ({ inputValue }) => {
            if (handleInputValueChange) {
                handleInputValueChange(inputValue || '');
            }
            else if (inputValue) {
                setComboboxControlledItems(comboboxItems.filter((item) => item.value.toLowerCase().includes(inputValue.toLowerCase())));
                if (inputItems.length === 1 && inputItems[0].value === inputValue) {
                    selectItem(inputItems[0]);
                }
            }
            else {
                setComboboxControlledItems(comboboxItems);
                if (clearSelectionOnEmptyInput) {
                    selectItem(null);
                }
            }
        },
        onIsOpenChange: ({ isOpen }) => {
            if (isOpen && showAllValuesOnOpen) {
                if ((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value) === inputValue) {
                    setComboboxControlledItems(items);
                }
            }
        },
        onStateChange: ({ type }) => {
            if (inputValue && type === useCombobox.stateChangeTypes.InputBlur) {
                // find item which has the same value as the input value
                const item = comboboxItems.find((item) => item.value.toLowerCase() === inputValue.toLowerCase());
                if (item) {
                    selectItem(item);
                }
            }
            else if (inputValue === '' && type === useCombobox.stateChangeTypes.InputBlur) {
                selectItem(null);
            }
        },
        itemToString(item) {
            if (item && handleItemToString) {
                return handleItemToString(item);
            }
            return item ? item.value : '';
        },
        onSelectedItemChange: ({ selectedItem }) => {
            if (handleSelectedItemChange) {
                handleSelectedItemChange(selectedItem);
            }
            else if (handleControlledValueChange) {
                handleControlledValueChange((selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value) || '');
            }
        },
        initialInputValue: controlledValue ? controlledValue : value,
        initialSelectedItem: controlledValue
            ? comboboxItems.find((item) => {
                if (item.value === controlledValue) {
                    if (handleSelectedItemChange) {
                        handleSelectedItemChange(item);
                    }
                    return true;
                }
                return false;
            })
            : null,
    });
    const ComboboxMenuItemComponent = menuItemComponent || DefaultComboboxMenuItem;
    const iconBeforeDefault = { componentFn: Search, ariaLabel: searchIconLabel };
    return (jsx(ComboboxContainer, { "$isDisabled": isDisabled, children: jsx(TextInput, Object.assign({ label: label, labelOptions: Object.assign(Object.assign({ style: { display: 'inline-block' } }, labelOptions), getLabelProps()), success: success, errorMessage: errorMessage, description: description, placeholder: placeholder, autocomplete: autocomplete, type: type, isDisabled: isDisabled, icon: icon || {
                componentFn: ChevronDown,
                ariaLabel: toggleButtonText,
                props: Object.assign({}, getToggleButtonProps()),
            }, iconBefore: iconBefore || iconBeforeDefault, showLoadingIndicator: loading, iconClear: inputValue
                ? {
                    componentFn: CloseSmall,
                    ariaLabel: clearText,
                    onClick: () => {
                        var _a;
                        const inputId = getInputProps().id;
                        setInputValue('');
                        selectItem(null);
                        (_a = document === null || document === void 0 ? void 0 : document.getElementById(inputId)) === null || _a === void 0 ? void 0 : _a.focus();
                    },
                }
                : undefined }, getInputProps(ref ? { ref: ref } : {}), other, { children: jsx(ComboboxMenu, Object.assign({}, getMenuProps(), { "$menuSize": menuSize, "$isOpen": isOpen, "$menuMaxHeight": menuMaxHeight, children: isOpen && loading ? (jsxs(ComboboxMenuItem, { "$isDisabled": true, "$menuSize": menuSize, children: [jsx(IconWrapper, { "$menuSize": "md", children: jsx(Spinner, { size: "sm" }) }), ' ', loadingText] })) : isOpen && comboboxControlledItems.length === 0 ? (jsx(ComboboxMenuItem, { "$isDisabled": true, "$menuSize": menuSize, children: noResultsText })) : (comboboxControlledItems.map((item, index = 1) => (jsx(ComboboxMenuItemComponent, { highlightedIndex: highlightedIndex, selectedItem: selectedItem, item: item, index: index, itemProps: getItemProps({ item, index }), menuSize: menuSize, children: item.value }, `${item.value}${index}`)))) })) })) }));
});

const MenuItem = (_a) => {
    var { icon, iconHighlight, isDisabled = false, isSelected = false, onSelect, closeMenuOnClick = true, role = 'menuitem', children, onClick, onKeyDown } = _a, other = __rest(_a, ["icon", "iconHighlight", "isDisabled", "isSelected", "onSelect", "closeMenuOnClick", "role", "children", "onClick", "onKeyDown"]);
    const menu = useContext(MenuContext);
    const handleSelect = useCallback(() => {
        if (onSelect) {
            onSelect();
        }
        if (closeMenuOnClick && menu.closeMenu) {
            menu === null || menu === void 0 ? void 0 : menu.closeMenu();
        }
    }, [closeMenuOnClick, menu, onSelect]);
    const handleClick = (e) => {
        if (isDisabled) {
            return;
        }
        if (onClick) {
            onClick(e);
        }
        e.preventDefault();
        handleSelect();
    };
    const handleKeyDown = (e) => {
        if (isDisabled) {
            return;
        }
        if (onKeyDown) {
            onKeyDown(e);
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSelect();
        }
    };
    return (jsxs(MenuItemBase, Object.assign({ onKeyDown: handleKeyDown, "$isDisabled": isDisabled, onClick: handleClick, tabIndex: -1, "aria-disabled": isDisabled ? 'true' : undefined, "aria-selected": isSelected ? 'true' : undefined, role: role, "$menuSize": menu.menuSize }, other, { children: [icon !== undefined && (jsx(IconWrapper, { "$highlight": iconHighlight, "$menuSize": menu.menuSize, children: icon })), children] })));
};
const MenuSeparator = () => jsx(MenuSeparatorBase, { role: "separator" });

const DropdownMenu = (_a) => {
    var _b;
    var { placeholder, value, options, onChange } = _a, menuProps = __rest(_a, ["placeholder", "value", "options", "onChange"]);
    const selectedOption = value !== undefined ? options.find((option) => option.value === value) : undefined;
    return (jsx(ActionMenu, Object.assign({}, menuProps, { text: (_b = selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.text) !== null && _b !== void 0 ? _b : placeholder, children: options.map((option) => {
            const isSelected = option.value === value;
            return (jsx(MenuItem, { icon: isSelected ? jsx(Check, {}) : null, iconHighlight: true, isSelected: isSelected, role: "menuitemradio", onSelect: () => onChange(option.value), children: option.text }, option.text));
        }) })));
};

const backgroundColor = ({ variant, level, theme, }) => {
    var _a, _b;
    const backgroundColorMap = {
        info: {
            primary: theme.BACKGROUND_INFO_PRIMARY,
            secondary: theme.BACKGROUND_INFO_SECONDARY,
            text: 'transparent',
        },
        success: {
            primary: theme.BACKGROUND_SUCCESS,
            secondary: theme.BACKGROUND_SUCCESS,
            text: 'transparent',
        },
        warning: {
            primary: theme.BACKGROUND_WARNING,
            secondary: theme.BACKGROUND_WARNING,
            text: 'transparent',
        },
        error: {
            primary: theme.BACKGROUND_ERROR,
            secondary: theme.BACKGROUND_ERROR,
            text: 'transparent',
        },
    };
    return (_b = (_a = backgroundColorMap[level]) === null || _a === void 0 ? void 0 : _a[variant]) !== null && _b !== void 0 ? _b : 'transparent';
};
const getColor = ({ theme, level, variant, }) => {
    var _a, _b;
    const colorMap = {
        info: {
            primary: color.GRAY_95,
            secondary: theme.TEXT_DEFAULT,
            text: theme.TEXT_DEFAULT,
        },
        success: {
            primary: color.GRAY_95,
            secondary: color.GRAY_95,
            text: theme.TEXT_DEFAULT,
        },
        warning: {
            primary: color.GRAY_95,
            secondary: color.GRAY_95,
            text: theme.TEXT_DEFAULT,
        },
        error: {
            primary: color.GRAY_95,
            secondary: color.GRAY_95,
            text: theme.TEXT_DEFAULT,
        },
    };
    return (_b = (_a = colorMap[level]) === null || _a === void 0 ? void 0 : _a[variant]) !== null && _b !== void 0 ? _b : color.GRAY_95;
};
const NotificationBase = styled.div(({ theme, $level, $variant }) => ({
    display: 'block',
    borderRadius: 8,
    backgroundColor: backgroundColor({ level: $level, variant: $variant, theme: theme.yds }),
    fontFamily: `'${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif`,
    padding: '13px 17px',
    fontSize: 14,
    fontWeight: 400,
    color: getColor({ level: $level, variant: $variant, theme: theme.yds }),
}));
const NotificationHeader = styled.div({
    lineHeight: 1.5,
    paddingTop: '1px',
    flexGrow: '1',
});
const NotificationTitle = styled.div(({ $isMultiline }) => ({
    display: $isMultiline ? 'block' : 'inline-block',
    fontWeight: 700,
    marginRight: '5px',
}));
const NotificationContent = styled.div({
    display: 'flex',
    alignItems: 'start',
});
const NotificationText = styled.div(({ $isMultiline }) => ({
    display: $isMultiline ? 'flex' : 'inline-flex',
    flex: 1,
}));
const NotificationIcon = styled.div({
    marginRight: '13px',
    '& svg': {
        verticalAlign: 'middle',
    },
});
const NotificationCloseButton = styled(Button)(({ theme, $level, $notificationVariant }) => ({
    border: '',
    minHeight: 0,
    minWidth: 0,
    marginTop: '-2px',
    marginBottom: '-2px',
    paddingTop: '0',
    paddingBottom: '0',
    color: getColor({ level: $level, variant: $notificationVariant, theme: theme.yds }),
    '&:focus': {
        outlineColor: $notificationVariant !== 'text'
            ? getColor({ level: $level, variant: $notificationVariant, theme: theme.yds })
            : '',
    },
}));
const NotificationActionsContainer = styled.div(({ theme, $level, $notificationVariant }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: '30px',
    '& button:focus': {
        outlineColor: $notificationVariant !== 'text'
            ? getColor({ level: $level, variant: $notificationVariant, theme: theme.yds })
            : '',
    },
}));

const YdsThemeProvider = ({ theme: themeProp = 'light', unit = 'rem', baseUnit = 16, children, }) => {
    const usingPresetTheme = typeof themeProp === 'string';
    const themeName = usingPresetTheme ? themeProp : 'custom';
    const theme = usingPresetTheme
        ? themeProp === 'dark'
            ? darkTheme
            : themeProp === 'light'
                ? lightTheme
                : defaultTheme
        : themeProp;
    return (jsx(ThemeContext.Provider, { value: { unit, baseUnit }, children: jsx(ThemeProvider, { theme: { ydsThemeName: themeName, yds: theme }, children: children }) }));
};

const getIcon = (level) => {
    switch (level) {
        case 'error':
            return jsx(AlertTriangle, {});
        case 'warning':
            return jsx(AlertCircle, {});
        case 'success':
            return jsx(Check, {});
        default:
            return jsx(Info, {});
    }
};
const Notification = (_a) => {
    var { actions, children, hideIcon, isMultiline, level = 'info', onClose, title, variant = 'primary' } = _a, other = __rest(_a, ["actions", "children", "hideIcon", "isMultiline", "level", "onClose", "title", "variant"]);
    const theme = useTheme();
    const ydsTheme = theme.ydsThemeName === 'custom' ? theme.yds : theme.ydsThemeName;
    return (jsx(NotificationBase, Object.assign({ "$level": level, "$variant": variant }, other, { children: jsxs(YdsThemeProvider, { theme: level === 'info' && variant === 'primary' ? 'light' : ydsTheme, children: [jsxs(NotificationContent, { children: [!hideIcon && jsx(NotificationIcon, { children: getIcon(level) }), jsxs(NotificationHeader, { children: [title && jsx(NotificationTitle, { "$isMultiline": isMultiline, children: title }), jsx(NotificationText, { "$isMultiline": isMultiline, children: children })] }), onClose && (jsx(NotificationCloseButton, { "$level": level, "$notificationVariant": variant, accessibleText: "Sulje", onClick: onClose, variant: "text", size: "md", iconBefore: jsx(Close, {}), iconOnly: true, removePadding: true }))] }), actions && (jsx(NotificationActionsContainer, { "$level": level, "$notificationVariant": variant, children: actions }))] }) })));
};

const SelectFieldset = styled.fieldset `
  border: none;
  padding: 0;
  margin: 0;
  display: block;
`;
const SelectWrapper = styled.div `
  position: relative;
  min-width: 150px;
  border-radius: 8px;
  &::after {
    content: '';
    position: absolute;
    right: 12px;
    top: 12px;
    width: 24px;
    height: 24px;
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'%3E%3Cpath d='M5.293 9.293a1 1 0 0 1 1.414 0L12 14.586l5.293-5.293a1 1 0 1 1 1.414 1.414l-6 6a1 1 0 0 1-1.414 0l-6-6a1 1 0 0 1 0-1.414Z' fill='currentColor'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'%3E%3Cpath d='M5.293 9.293a1 1 0 0 1 1.414 0L12 14.586l5.293-5.293a1 1 0 1 1 1.414 1.414l-6 6a1 1 0 0 1-1.414 0l-6-6a1 1 0 0 1 0-1.414Z' fill='currentColor'/%3E%3C/svg%3E");
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
    background-color: ${(props) => props.$isDisabled ? props.theme.yds.TEXT_DISABLED : props.theme.yds.TEXT_DEFAULT};
    pointer-events: none;
  }
`;
const determineBorderColor$1 = (defaultValue) => (props) => {
    if (props.$error) {
        return props.theme.yds.FEEDBACK_ERROR;
    }
    else if (props.$success) {
        return props.theme.yds.FEEDBACK_SUCCESS;
    }
    else {
        return defaultValue;
    }
};
const SelectElement = styled.select `
  appearance: none;
  padding: 4px 40px 4px 16px;
  font-family: '${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif;
  font-size: 16px;
  margin: 0;
  border: 2px solid ${(props) => determineBorderColor$1(props.theme.yds.BORDER)};
  height: 48px;
  width: 100%;
  background-color: ${(props) => props.theme.yds.BACKGROUND_INPUT};
  outline: none;
  border-radius: 8px;
  color: ${(props) => props.theme.yds.TEXT_DEFAULT};

  &::-ms-expand {
    display: none;
  }

  &:focus,
  &:active {
    outline: ${({ theme }) => `3px solid ${theme.yds.BORDER_PRIMARY_FOCUS}`};
    outline-offset: 2px;
  }

  &:required:invalid {
    color: ${(props) => props.theme.yds.TEXT_INPUT_PLACEHOLDER};
  }

  &:disabled {
    color: ${(props) => props.theme.yds.TEXT_DISABLED};
    border-color: ${(props) => props.theme.yds.BORDER_DISABLED};
    box-shadow: none;
  }
`;
const OptionElement = styled.option `
  &:disabled {
    color: ${(props) => props.theme.yds.TEXT_DISABLED};
  }
  &[hidden]:disabled {
  }
`;
const FormElementDescription = styled.p `
  color: ${(props) => props.theme.yds.TEXT_DEFAULT};
  font-family: '${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif;
  text-decoration: none;
  font-size: 14px;
  line-height: 150%;
  margin: 8px 0 0 0;
`;
const FormElementErrorMessage = styled(FormElementDescription) `
  color: ${(props) => props.theme.yds.FEEDBACK_ERROR};
`;

/**
 * A native web select component with a label and optional error and description messages.
 * extra params are passed to the underlying input component as-is, except for `aria-disabled`, which
 * is controlled by `isDisabled` and `aria-labelled-by`, which is generated from `id`.
 * Dropdown option selection UI and behavior determined by browser.
 *
 * @param {SelectProps} props
 * */
const Select = forwardRef(({ isDisabled = false, success = false, items = [], label = 'Label', labelOptions = {}, placeholder, errorMessage, description, value, id, name, onChange, }, ref) => (jsxs(SelectFieldset, { children: [jsx(FormElementLabel, Object.assign({ isDisabled: isDisabled, isRequired: false, htmlFor: id, label: label }, labelOptions)), jsx(SelectWrapper, { "$isDisabled": isDisabled, className: "yds-select__wrapper", children: jsxs(SelectElement, { ref: ref, disabled: isDisabled, required: !!placeholder, name: name, "aria-disabled": isDisabled, onChange: onChange, "$success": !errorMessage && success, "$error": Boolean(errorMessage), value: value ? value : placeholder ? '' : '', id: id, children: [placeholder && (jsx(OptionElement, { value: "", hidden: true, children: placeholder }, 'placeholder-value')), items.map((item) => (jsx(OptionElement, { value: item.value ? item.value : item.label, disabled: item.disabled, children: item.label }, item.value)))] }) }), Boolean(errorMessage) && jsx(FormElementErrorMessage, { children: errorMessage }), Boolean(description) && jsx(FormElementDescription, { children: description })] })));

styled.fieldset `
  border: none;
  margin: 0;
  padding: 0;
`;
const SwitchBase = styled.input(({ theme }) => css `
    appearance: none;
    display: flex;
    align-items: center;
    height: 24px;
    width: 44px;
    border-radius: 24px;
    background-color: ${theme.yds.BACKGROUND_CONTROL};
    padding: 3px;
    margin: 0;
    cursor: pointer;
    transition: background-color 0.15s linear;

    &::before {
      content: '';
      background-color: ${theme.yds.TEXT_NEGATIVE};
      width: 18px;
      height: 18px;
      border-radius: 19px;
      position: absolute;
      transition: transform 0.15s cubic-bezier(0.41, 0, 0.24, 1);
    }

    &:checked {
      background-color: ${theme.yds.FEEDBACK_HIGHLIGHT};
    }

    &:checked::before {
      transform: translateX(20px);
    }

    &:disabled {
      background-color: ${theme.yds.BACKGROUND_CONTROL_DISABLED};
    }
    &:focus {
      outline: none;
      box-shadow:
        0px 0px 0px 2px ${theme.yds.BACKGROUND},
        0px 0px 0px 4px ${theme.yds.ACTION_PRIMARY};
    }
    @supports selector(:focus-visible) {
      &:focus {
        box-shadow: none;
      }
      &:focus-visible {
        outline: none;
        box-shadow:
          0px 0px 0px 2px ${theme.yds.BACKGROUND},
          0px 0px 0px 4px ${theme.yds.ACTION_PRIMARY};
      }
    }
  `);

/**
 * Switch (or Toggle, Toggle button, Toggle switch) is a on-off selection component with a label.
 * Extra params are passed to the underlying input component as-is, except for `aria-disabled`, which
 * is controlled by `isDisabled`. Label text and Switch are linked together through component `id`.
 * Switch should be used in situation when user changing the value causes instant and persistent change -
 * for on/off selections that need to be submitted separately, use `Checkbox` component.
 *
 * @param {SwitchProps} props
 * */
const Switch = forwardRef((_a, ref) => {
    var { isDisabled = false, label, labelOptions = {}, name, onChange } = _a, other = __rest(_a, ["isDisabled", "label", "labelOptions", "name", "onChange"]);
    const id = other.id;
    return (jsxs(Fragment, { children: [!!label && (jsx(FormElementLabel, Object.assign({ id: `${name}--label`, htmlFor: id, isDisabled: isDisabled, label: label }, labelOptions))), jsx(SwitchBase, Object.assign({ ref: ref, type: "checkbox", disabled: isDisabled, name: name, "aria-disabled": isDisabled, role: "switch", onChange: onChange }, other))] }));
});

const isTextTransform = (value) => ['capitalize', 'full-size-kana', 'full-width', 'lowercase', 'uppercase', 'none'].includes(value);
const getTextTransform = (typographyToken) => typeof typographyToken === 'string' && isTextTransform(typographyToken)
    ? typographyToken
    : undefined;
const glow = keyframes `
  0% {
    transform: scaleX(1);
    opacity: 0;
  }
  42% {
    transform: scaleX(1);
    opacity: 0.7;
  }
  100% {
    transform: scaleY(1.6) scaleX(1.3);
    opacity: 0;
  }
`;
const getBackgroundColor$1 = ({ theme, variant, themeName, }) => {
    switch (variant) {
        case 'live':
        case 'rightnow':
        case 'online':
        case 'chat':
            return theme.TAG_LIVE;
        case 'opinion':
            return theme.TAG_OPINION;
        case 'ylecorporate':
            return theme.TAG_YLE_CORPORATE;
        case 'negative':
            return theme.BACKGROUND;
        case 'transparent':
            return 'transparent';
        case 'red':
            return color.RED_50;
        case 'purple':
            return themeName === 'dark' ? color.PURPLE_50 : color.PURPLE_80;
        case 'turquoise':
            return color.TURQUOISE_80;
        case 'default':
        default:
            return theme.ACTION_PRIMARY;
    }
};
const getTextColor$1 = ({ theme, variant }) => {
    switch (variant) {
        case 'live':
        case 'rightnow':
        case 'online':
        case 'chat':
        case 'opinion':
        case 'ylecorporate':
            return theme.TEXT_LIGHT;
        case 'negative':
            return theme.TEXT_DEFAULT;
        case 'transparent':
            return theme.TEXT_DEFAULT;
        case 'red':
        case 'purple':
        case 'turquoise':
            return color.WHITE;
        case 'default':
        default:
            return theme.TEXT_NEGATIVE;
    }
};
const AnimatedTag = styled.span `
  @media (prefers-reduced-motion: no-preference) {
    &::before {
      position: absolute;
      inset: 5px;
      border-radius: 2px;
      box-shadow: ${({ $variant, theme }) => `0 0 1px 5px
      ${getBackgroundColor$1({
    theme: theme.yds,
    variant: $variant,
    themeName: theme.ydsThemeName,
})}`};
      /* Animation follows in-out-quad easing of react-native-reanimated */
      animation: 2.4s cubic-bezier(0.455, 0.03, 0.515, 0.955) 0s infinite normal none running
        ${glow};
    }
  }
`;
const TagBase = styled(AnimatedTag)(({ theme, $size, $variant, $preventTransformUppercase, $animate }) => ({
    display: 'inline-block',
    backgroundColor: getBackgroundColor$1({
        theme: theme.yds,
        variant: $variant,
        themeName: theme.ydsThemeName,
    }),
    color: getTextColor$1({ theme: theme.yds, variant: $variant }),
    fontFamily: `"${typography.DEFAULT_XS_BOLD_AC_FONT_FAMILY}", "Open Sans", sans-serif`,
    fontWeight: typography.DEFAULT_XS_BOLD_AC_FONT_WEIGHT,
    fontSize: typography.DEFAULT_XS_BOLD_AC_FONT_SIZE,
    fontStyle: typography.DEFAULT_XS_BOLD_AC_FONT_STYLE,
    lineHeight: $size === 'sm' ? '18px' : typography.DEFAULT_XS_BOLD_AC_LINE_HEIGHT,
    letterSpacing: typography.DEFAULT_XS_BOLD_AC_LETTER_SPACING,
    textTransform: $preventTransformUppercase
        ? 'none'
        : getTextTransform(typography.DEFAULT_XS_BOLD_AC_TEXT_CASE),
    borderRadius: radius.MEDIUM,
    padding: $size === 'sm' ? '1px 8px' : '3px 8px',
    maxHeight: $size === 'sm' ? '20px' : '24px',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    /* This is needed to allow animation glow overflowing from Live tag */
    overflow: $variant === 'live' && $animate === true ? 'visible' : 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center',
    position: 'relative',
    '&::before': {
        content: $variant === 'live' && $animate === true ? '""' : '',
    },
}));

/**
 * A tag component with a text label with various presentation options.
 * Used to convey short keywords in noticeable manner.
 *
 * @param {TagProps} props
 * @param {TagSizeProp} [props.size='md'] - Size of the tag
 * */
const Tag = (_a) => {
    var { size = 'md', variant = 'default', preventTransformUppercase = false, animate = false, children } = _a, other = __rest(_a, ["size", "variant", "preventTransformUppercase", "animate", "children"]);
    return (jsx(TagBase, Object.assign({ "$variant": variant, "$size": size, "$preventTransformUppercase": preventTransformUppercase, "$animate": animate }, other, { children: children })));
};

const getBackgroundColor = (theme, variant) => {
    switch (variant) {
        case 'text':
            return 'transparent';
        case 'secondary':
            return theme.TAG_SECONDARY_ACTION;
        case 'primary':
        default:
            return theme.TAG_PRIMARY_ACTION;
    }
};
const getHoverBackgroundColor = (theme, variant, removePadding) => {
    if (removePadding) {
        return getBackgroundColor(theme, variant);
    }
    switch (variant) {
        case 'text':
            return theme.TAG_SECONDARY_ACTION_VARIANT;
        case 'secondary':
            return theme.TAG_SECONDARY_ACTION_VARIANT;
        case 'primary':
        default:
            return theme.TAG_PRIMARY_ACTION_VARIANT;
    }
};
const getTextColor = (theme, variant) => {
    switch (variant) {
        case 'text':
            return theme.TEXT_DEFAULT;
        case 'secondary':
            return theme.TEXT_DEFAULT;
        case 'primary':
        default:
            return theme.TEXT_NEGATIVE;
    }
};
const getTagLinkBaseStyles = ({ theme, $variant, $removePadding, }) => ({
    display: 'inline-block',
    backgroundColor: getBackgroundColor(theme.yds, $variant),
    color: getTextColor(theme.yds, $variant),
    padding: $removePadding && $variant === 'text' ? '3px 0' : '3px 12px 3px',
    borderRadius: $removePadding ? radius.LARGE : radius.FULL,
    fontFamily: `"${typography.DEFAULT_XS_BOLD_FONT_FAMILY}", "Open Sans", sans-serif`,
    fontWeight: typography.DEFAULT_XS_BOLD_FONT_WEIGHT,
    fontSize: typography.DEFAULT_XS_BOLD_FONT_SIZE,
    fontStyle: typography.DEFAULT_XS_BOLD_FONT_STYLE,
    lineHeight: typography.DEFAULT_XS_BOLD_LINE_HEIGHT,
    letterSpacing: typography.DEFAULT_XS_BOLD_LETTER_SPACING,
    textDecoration: 'none',
});
const TagLinkBase = styled.a(({ theme, $variant, $removePadding }) => (Object.assign(Object.assign({}, getTagLinkBaseStyles({ theme, $variant, $removePadding })), { '&:hover': {
        backgroundColor: getHoverBackgroundColor(theme.yds, $variant, $removePadding),
    }, '@supports selector(:focus-visible)': {
        '&:focus': {
            outline: 'none',
        },
        '&:focus-visible': {
            outline: `3px solid ${theme.yds.BORDER_PRIMARY_FOCUS}`,
            outlineOffset: '2px',
        },
    } })));
const TagLinkBaseNonInteractive = styled.span(({ theme, $variant, $removePadding }) => (Object.assign({}, getTagLinkBaseStyles({ theme, $variant, $removePadding }))));

/**
 * An interactive keyword link that provides context for the title.
 * Tag links are used as a group at the end of the article.
 *
 * @param {TagLinkProps} props
 * */
const TagLink = (_a) => {
    var { variant = 'primary', removePadding = false, children } = _a, other = __rest(_a, ["variant", "removePadding", "children"]);
    if (!other.href && !other.onClick) {
        return (jsx(TagLinkBaseNonInteractive, Object.assign({ "$variant": variant, "$removePadding": removePadding }, other, { children: children })));
    }
    return (jsx(TagLinkBase, Object.assign({ "$variant": variant, "$removePadding": removePadding }, other, { children: children })));
};

const TextareaFieldset = styled.fieldset `
  border: none;
  margin: 0;
  padding: 0;
`;
const determineBorderColor = (defaultValue) => (props) => {
    if (props.$error) {
        return props.theme.yds.FEEDBACK_ERROR;
    }
    else if (props.$success) {
        return props.theme.yds.FEEDBACK_SUCCESS;
    }
    else {
        return defaultValue;
    }
};
const StyledTextarea = styled.textarea `
  align-items: center;
  background-color: ${(props) => props.theme.yds.BACKGROUND_INPUT};
  border: 2px solid ${(props) => determineBorderColor(props.theme.yds.BORDER)};
  border-radius: 8px;
  color: ${(props) => props.theme.yds.TEXT_DEFAULT};
  font-family: '${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif;
  font-size: 16px;
  line-height: 24px;
  text-decoration: none;
  display: flex;
  flex-direction: row;
  outline: none;
  padding: ${spacing.SPACING_12} ${spacing.SPACING_16};
  width: 100%;
  resize: none;
  color-scheme: ${({ theme }) => (theme.ydsThemeName === 'dark' ? 'dark' : 'light')};

  &:focus,
  &:active {
    outline: ${({ theme }) => `3px solid ${theme.yds.BORDER_PRIMARY_FOCUS}`};
    outline-offset: 2px;
  }

  &:disabled {
    color: ${(props) => props.theme.yds.TEXT_DISABLED};
    border: ${(props) => `2px solid ${props.theme.yds.TEXT_DISABLED}`};
    cursor: default;
  }

  &::placeholder {
    color: ${(props) => props.theme.yds.TEXT_INPUT_PLACEHOLDER};
  }

  &:disabled:focus,
  &:disabled:active {
    color: ${(props) => props.theme.yds.TEXT_DISABLED};
    box-shadow: none;
    background-color: ${(props) => props.theme.yds.BACKGROUND_INPUT};
  }

  &:disabled::placeholder {
    color: ${(props) => props.theme.yds.TEXT_DISABLED};
  }
`;
const TextareaDescription = styled.p `
  color: ${(props) => props.theme.yds.TEXT_DEFAULT};
  font-family: '${typographyTokens.FONT_FAMILY_DEFAULT}', 'Open Sans', sans-serif;
  text-decoration: none;
  font-size: 14px;
  line-height: 150%;
  margin: 8px 0 0 0;
`;
const TextareaErrorMessage = styled(TextareaDescription) `
  color: ${(props) => props.theme.yds.FEEDBACK_ERROR};
`;

/**
 * A textarea component with a label and optional error and description messages.
 * extra params are passed to the underlying textarea component as-is, except for `aria-disabled`, which
 * is controlled by `isDisabled` and `aria-labelled-by`, which is generated from `id`.
 *
 * @param {TextareaProps} props
 * */
const Textarea = forwardRef((_a, ref) => {
    var { label, labelOptions = {}, id, value, onChange, placeholder, rows, description, errorMessage, success = false, isDisabled = false } = _a, other = __rest(_a, ["label", "labelOptions", "id", "value", "onChange", "placeholder", "rows", "description", "errorMessage", "success", "isDisabled"]);
    return (jsxs(TextareaFieldset, { children: [jsx(FormElementLabel, Object.assign({ htmlFor: id, id: `${id}-label`, isDisabled: isDisabled }, labelOptions, { children: label })), jsx(StyledTextarea, Object.assign({ id: id, ref: ref, value: value, onChange: onChange, placeholder: placeholder, rows: rows, "$error": Boolean(errorMessage), "$success": !errorMessage && success, disabled: isDisabled, "aria-disabled": isDisabled, "aria-labelledby": `${id}-label` }, other)), Boolean(errorMessage) && jsx(TextareaErrorMessage, { children: errorMessage }), Boolean(description) && jsx(TextareaDescription, { children: description })] }));
});

const StyledFieldset = styled.fieldset({
    border: 0,
    padding: 0,
    margin: 0,
});
const StyledRadioGroup = styled.div({
    display: 'flex',
    flexDirection: 'column',
    '& > label:not(:last-child)': {
        marginBottom: 8,
    },
    [`&:not(:only-child)`]: {
        marginTop: 8,
    },
});
const StyledLabel = styled.legend(({ theme }) => ({
    color: theme.yds.TEXT_DEFAULT,
    marginBottom: 8,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5,
    margin: 0,
    padding: 0,
}));
const StyledDescription = styled.div(({ theme }) => ({
    color: theme.yds.TEXT_DEFAULT,
    fontSize: 14,
    lineHeight: 1.5,
}));

const RadioGroup = forwardRef((props, ref) => {
    const { children, name, selectedValue: valueProp, id: idProp, errorMessage, label, description, onChange } = props, rest = __rest(props, ["children", "name", "selectedValue", "id", "errorMessage", "label", "description", "onChange"]);
    const [value, setValueState] = useState(valueProp);
    const useFieldset = label || description;
    const hasError = Boolean(errorMessage);
    const id = idProp || `group-${name}`;
    useEffect(() => {
        setValueState(valueProp);
    }, [valueProp]);
    const handleChange = (event) => {
        setValueState(event.target.value);
        if (onChange) {
            onChange(event);
        }
    };
    const renderRadioGroup = () => (jsx(StyledRadioGroup, { role: "radiogroup", "aria-label": label, ref: ref, children: children }));
    return (jsx(RadioGroupContext.Provider, { value: {
            name,
            onChange: handleChange,
            value,
            error: hasError,
            errorId: hasError ? `${id}-error` : undefined,
            descId: description ? `${id}-desc` : undefined,
        }, children: useFieldset ? (jsxs(StyledFieldset, Object.assign({}, rest, { children: [label && jsx(StyledLabel, { children: label }), description && jsx(StyledDescription, { id: `${id}-desc`, children: description }), renderRadioGroup(), hasError && (jsx(Message, { id: `${id}-error`, "$error": true, children: errorMessage }))] }))) : (jsxs(Fragment, { children: [renderRadioGroup(), hasError && (jsx(Message, { id: `${id}-error`, "$error": true, children: errorMessage }))] })) }));
});

/**
 * @deprecated Use exported `XXXcssFragment` from `@yleisradio/yds-core` instead.
 *
 * Converts a token name to a corresponding CSS variable name.
 */
const colorTokenToCssVar = (tokenName) => `--yds-color-${tokenName.toLowerCase().replace(/_/g, '-')}`;
/**
 * @deprecated Use exported `XXXcssFragment` from `@yleisradio/yds-core` instead.
 *
 * Converts a theme to collection of CSS variable declarations. Returns CSS,
 * like this:
 *
 * ```css
 * --yds-color-background: #ffffff;
 * --yds-color-background-variant: #f8f9fa;
 * <!-- rest of the properties -->
 * ```
 */
const themeToCssVars = (theme) => Object.entries(theme)
    // "default" module export might sometimes be in theme, depends on how it was imported.
    .filter(([key]) => key !== 'default')
    .map(([key, value]) => `${colorTokenToCssVar(key)}: ${value};`)
    .join('\n');

export { Accordion, AccordionItem, ActionMenu, ArticleHeading, Button, Checkbox, CheckboxGroup, ChoiceBox, ChoiceButton, ComboboxSingleSelect, DropdownMenu, FormElementLabel, MenuItem, MenuSeparator, Notification, Radio, RadioGroup, Select, Spinner, Switch, Tag, TagLink, TextInput, Textarea, YdsThemeProvider, colorTokenToCssVar, themeToCssVars };
